import type { DatasetBinding, FilterControl } from '../../../api/dashboards.js';
import type { DatasetSummary } from '../../../api/types.js';

/**
 * Inline editor for the cross-widget filter bar. Lives above the grid
 * in the designer. At render time, these controls broadcast to every
 * widget whose binding exposes the filtered field.
 */
export function FilterBarEditor({
  filters,
  datasets,
  datasetCatalog,
  onChange,
}: {
  filters: FilterControl[];
  datasets: DatasetBinding[];
  datasetCatalog: DatasetSummary[] | undefined;
  onChange(next: FilterControl[]): void;
}): JSX.Element {
  const allFields = new Set<string>();
  for (const b of datasets) {
    if (b.columns) b.columns.forEach((c) => allFields.add(c));
    const entry = datasetCatalog?.find((d) => d.name === b.datasetName);
    entry?.columns.forEach((c: { name: string }) => allFields.add(c.name));
  }
  const sortedFields = [...allFields].sort();

  return (
    <div className="filter-bar-editor">
      <div className="filter-bar-editor-header">
        <span>Cross-widget filters</span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() =>
            onChange([
              ...filters,
              {
                id: `f_${Date.now().toString(36)}`,
                kind: 'dateRange',
                label: 'Date range',
                field: sortedFields[0] ?? '',
              },
            ])
          }
        >
          + Add filter
        </button>
      </div>
      {filters.length === 0 && (
        <p className="filter-bar-editor-empty">
          No filters. Add one to let viewers narrow every widget with a single control.
        </p>
      )}
      {filters.map((f, i) => (
        <div key={f.id} className="filter-bar-editor-row">
          <input
            className="input input--sm"
            value={f.label}
            onChange={(e) => replaceAt(filters, i, { ...f, label: e.target.value }, onChange)}
            placeholder="Label"
          />
          <select
            className="select"
            value={f.kind === 'number' && f.range ? 'numberRange' : f.kind}
            onChange={(e) => replaceAt(filters, i, withKind(f, e.target.value), onChange)}
          >
            <option value="dateRange">Date range</option>
            <option value="enum">Enum</option>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="numberRange">Number range</option>
          </select>
          <select
            className="select"
            value={f.field}
            onChange={(e) => replaceAt(filters, i, { ...f, field: e.target.value }, onChange)}
          >
            <option value="">(field)</option>
            {sortedFields.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-icon"
            onClick={() => onChange(filters.filter((_, j) => j !== i))}
            aria-label="Remove filter"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/** CSAGE-598: "Number range" is kind 'number' with `range: true`. */
function withKind(f: FilterControl, choice: string): FilterControl {
  const { range: _range, ...rest } = f;
  if (choice === 'numberRange') return { ...rest, kind: 'number', range: true };
  return { ...rest, kind: choice as FilterControl['kind'] };
}

function replaceAt<T>(arr: T[], i: number, next: T, onChange: (v: T[]) => void): void {
  const copy = [...arr];
  copy[i] = next;
  onChange(copy);
}
